import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { ProposalContent, ProposalImages, AdditionalPhoto } from "@/types/proposal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProposalPage1 } from "@/components/proposal/ProposalPage1";
import { ProposalPage2 } from "@/components/proposal/ProposalPage2";
import { ProposalPage3 } from "@/components/proposal/ProposalPage3";

interface EmailProposalDialogProps {
  content: ProposalContent;
  images: ProposalImages;
  additionalPhotos: AdditionalPhoto[];
  creatorUserId: string;
}


export function EmailProposalDialog({ content, images, additionalPhotos, creatorUserId }: EmailProposalDialogProps) {
  const [open, setOpen] = useState(false);
  const [clientEmail, setClientEmail] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!clientEmail || !clientEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSending(true);
    try {
      // Snapshot the live proposal and clean it up for email
      const proposalElement = document.querySelector('.proposal-container');
      if (!proposalElement) {
        toast.error('Proposal not found');
        setIsSending(false);
        return;
      }

      const clone = proposalElement.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('[class*="border-dashed"], .signature-section, input, button, .print\\:hidden').forEach((el) => el.remove());

      // Inline images
      const imgs = Array.from(clone.querySelectorAll('img')) as HTMLImageElement[];
      await Promise.all(
        imgs.map(async (img) => {
          const src = img.getAttribute('src') || '';
          if (!src || src.startsWith('data:')) return;
          try {
            const res = await fetch(src);
            const blob = await res.blob();
            await new Promise<void>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                img.setAttribute('src', reader.result as string);
                resolve();
              };
              reader.readAsDataURL(blob);
            });
          } catch (_) {
            // ignore
          }
        })
      );

      const proposalHtml = clone.innerHTML;

      const { data, error } = await supabase.functions.invoke('send-proposal-email', {
        body: {
          to: clientEmail,
          clientName: content.clientName,
          projectTitle: content.title,
          proposalHtml,
          creatorUserId: creatorUserId,
          siteUrl: window.location.origin,
        },
      });

      // Prefer detailed error message from function response when available
      if (error) {
        let message = error.message || "Failed to send email";
        try {
          const response = (error as any)?.context?.response;
          if (response && typeof response.json === 'function') {
            const json = await response.json();
            message = json?.error || json?.message || message;
          }
        } catch (_) {
          // ignore JSON parse errors
        }
        throw new Error(message);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      console.log("Email sent successfully:", data);
      toast.success(`Proposal sent successfully to ${clientEmail}`);
      setOpen(false);
      setClientEmail("");
    } catch (error: any) {
      console.error("Error sending email:", error);
      const errorMessage = error?.message || "Failed to send email. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} size="sm" className="bg-blue-600 hover:bg-blue-700">
        <Mail className="w-4 h-4 mr-2" />
        Email to Client
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Proposal to Client</DialogTitle>
            <DialogDescription>
              Send this proposal directly to your client's email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="email">Client Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="client@example.com"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSend} disabled={isSending} className="flex-1">
                {isSending ? "Sending..." : "Send Proposal"}
              </Button>
              <Button onClick={() => setOpen(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
